import { useState, useEffect } from 'react'
import { usersAPI } from '../services/api'
import { useDashboard } from '../contexts/DashboardContext'
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

// Canonical plans exposed in the admin dropdown — must match backend
// CANONICAL_PLANS in api/admin/registrations.py. Order = display order.
const PLAN_OPTIONS = [
  { value: 'free',    label: 'Free' },
  { value: 'starter', label: 'Starter' }, // 7-day trial
  { value: 'growth',  label: 'Growth' },
  { value: 'pro',     label: 'Pro' },
]
// Labels for displaying ANY plan (including legacy values that may still
// exist on older customer rows). Falls back to title-case if unknown.
const PLAN_LABEL = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
  // legacy
  basic: 'Basic',
  premium: 'Premium',
  professional: 'Professional',
  enterprise: 'Enterprise',
}
const planLabel = (plan) =>
  PLAN_LABEL[plan] ||
  (plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : 'Free')

export default function Registrations() {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [filter, setFilter] = useState('all')
  const [planDropdownOpen, setPlanDropdownOpen] = useState(null)
  const [extendDays, setExtendDays] = useState(30)
  const [extendLoading, setExtendLoading] = useState(false)
  const { refreshStats } = useDashboard()

  useEffect(() => {
    loadCustomers()
  }, [filter])

  const loadCustomers = async () => {
    try {
      setLoading(true)
      // per_page=10000 — effectively "all customers" for any realistic admin scale.
      // Backend caps at 10000 (api/admin/registrations.py list_users).
      const params = { per_page: 10000, ...(filter !== 'all' ? { status: filter } : {}) }
      const response = await usersAPI.getAll(params)
      setCustomers(response.data.users || [])
    } catch (err) {
      console.error('Failed to load customers:', err)
      setError('Failed to load customers')
    } finally {
      setLoading(false)
    }
  }

  const handleActivate = async (customer) => {
    try {
      setActionLoading(true)
      await usersAPI.activate(customer.id, 'Activated by admin')
      await loadCustomers()
      refreshStats()
      setSelectedCustomer(null)
    } catch (err) {
      console.error('Failed to activate customer:', err)
      alert('Failed to activate customer')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeactivate = async (customer, reason) => {
    try {
      setActionLoading(true)
      await usersAPI.deactivate(customer.id, reason || 'Deactivated by admin')
      await loadCustomers()
      refreshStats()
      setSelectedCustomer(null)
    } catch (err) {
      console.error('Failed to deactivate customer:', err)
      alert('Failed to deactivate customer')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (customer) => {
    if (!window.confirm(`Are you sure you want to permanently delete the customer "${customer.full_name}"? This action cannot be undone.`)) {
      return
    }

    try {
      setActionLoading(true)
      await usersAPI.delete(customer.id)
      await loadCustomers()
      refreshStats()
      setSelectedCustomer(null)
      alert('Customer deleted successfully')
    } catch (err) {
      console.error('Failed to delete customer:', err)
      alert('Failed to delete customer')
    } finally {
      setActionLoading(false)
    }
  }

  const handleUpdatePlan = async (customer, newPlan) => {
    if (customer.subscription_plan === newPlan) {
      setPlanDropdownOpen(null)
      return
    }

    const planLabel = newPlan.charAt(0).toUpperCase() + newPlan.slice(1)
    if (!window.confirm(`Change ${customer.full_name}'s plan to "${planLabel}"?`)) {
      setPlanDropdownOpen(null)
      return
    }

    try {
      setActionLoading(true)
      await usersAPI.updatePlan(customer.id, newPlan)
      await loadCustomers()
      refreshStats()
      setPlanDropdownOpen(null)
      if (selectedCustomer && selectedCustomer.id === customer.id) {
        setSelectedCustomer(prev => ({ ...prev, subscription_plan: newPlan }))
      }
    } catch (err) {
      console.error('Failed to update plan:', err)
      const detail = err.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : err.message)
      alert('Failed to update plan: ' + msg)
    } finally {
      setActionLoading(false)
    }
  }

  const handleExtendTrial = async (customer) => {
    if (!extendDays || extendDays < 1) {
      alert('Please enter a valid number of days')
      return
    }
    if (!window.confirm(`Extend ${customer.full_name}'s trial by ${extendDays} days?`)) {
      return
    }
    try {
      setExtendLoading(true)
      const res = await usersAPI.extendTrial(customer.id, extendDays)
      await loadCustomers()
      // Update the selected customer with new end date
      const newEndDate = res.data?.user?.subscription_end_date
      if (newEndDate) {
        setSelectedCustomer(prev => ({ ...prev, subscription_end_date: newEndDate }))
      }
      alert(`Trial extended by ${extendDays} days successfully`)
    } catch (err) {
      console.error('Failed to extend trial:', err)
      const detail = err.response?.data?.detail
      alert('Failed to extend trial: ' + (typeof detail === 'string' ? detail : err.message))
    } finally {
      setExtendLoading(false)
    }
  }

  const getPlanBadgeClasses = (plan) => {
    switch (plan) {
      case 'free': return 'bg-slate-100 text-slate-700'
      case 'starter': return 'bg-amber-50 text-amber-700'    // 7-day trial
      case 'growth': return 'bg-brand-50 text-brand-700'
      case 'pro': return 'bg-purple-100 text-purple-800'
      // legacy values that may still exist on older customer rows
      case 'basic': return 'bg-blue-50 text-blue-700'
      case 'premium': return 'bg-amber-50 text-amber-700'
      case 'professional': return 'bg-brand-50 text-brand-700'
      case 'enterprise': return 'bg-purple-100 text-purple-800'
      default: return 'bg-slate-100 text-slate-700'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" />
      case 'terminated':
        return <XCircleIcon className="h-5 w-5 text-red-600" />
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />
    }
  }

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
    switch (status) {
      case 'approved':
        return `${baseClasses} bg-green-50 text-green-700`
      case 'rejected':
        return `${baseClasses} bg-red-50 text-red-700`
      case 'terminated':
        return `${baseClasses} bg-red-200 text-red-900`
      default:
        return `${baseClasses} bg-yellow-100 text-yellow-800`
    }
  }

  if (loading && !customers.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Customer Management</h1>
            <p className="text-slate-600">Manage customer accounts and subscriptions</p>
          </div>
          <div className="flex space-x-4">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-slate-300 rounded-xl px-4 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="unverified">Unverified</option>
            </select>
            <button
              onClick={loadCustomers}
              className="bg-brand-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-brand-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}

      {/* Customers Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Trial</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Registered</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedCustomer(customer)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">{customer.full_name}</div>
                    <div className="text-sm text-slate-500">{customer.email}</div>
                    {customer.api_key_created && (
                      <div className="text-xs text-green-600 flex items-center mt-1">
                        API Key Active
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900">{customer.company || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setPlanDropdownOpen(planDropdownOpen === customer.id ? null : customer.id)
                      }}
                      disabled={actionLoading}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-brand-300 transition-all ${getPlanBadgeClasses(customer.subscription_plan)}`}
                      title="Click to change plan"
                    >
                      {planLabel(customer.subscription_plan)}
                      <svg className="h-3 w-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {planDropdownOpen === customer.id && (
                      <div className="absolute z-20 mt-1 w-36 bg-white rounded-md border border-slate-200 shadow-lg">
                        {PLAN_OPTIONS.map(({value: plan, label}) => (
                          <button
                            key={plan}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUpdatePlan(customer, plan)
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-100 first:rounded-t-md last:rounded-b-md ${
                              customer.subscription_plan === plan ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-700'
                            }`}
                          >
                            {label}
                            {customer.subscription_plan === plan && ' (current)'}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">{customer.monthly_conversations_used || 0} / {customer.monthly_conversations_limit || 1000}</div>
                    <div className="w-16 bg-slate-200 rounded-full h-1 mt-1">
                      <div
                        className="bg-brand-600 h-1 rounded-full"
                        style={{
                          width: `${Math.min((customer.monthly_conversations_used || 0) / (customer.monthly_conversations_limit || 1000) * 100, 100)}%`
                        }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      !customer.is_verified ? 'bg-yellow-100 text-yellow-800' :
                      customer.is_active ? 'bg-green-50 text-green-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      {!customer.is_verified ? 'Unverified' :
                       customer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {customer.subscription_plan === 'starter' && customer.subscription_end_date ? (() => {
                      const days = Math.max(0, Math.ceil((new Date(customer.subscription_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          days === 0 ? 'bg-red-100 text-red-800' :
                          days <= 5 ? 'bg-red-50 text-red-700' :
                          days <= 10 ? 'bg-amber-50 text-amber-700' :
                          'bg-blue-50 text-blue-700'
                        }`}>
                          {days === 0 ? 'Expired' : days + 'd left'}
                        </span>
                      )
                    })() : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={(e) => {e.stopPropagation(); setSelectedCustomer(customer)}}
                        className="text-brand-600 hover:text-brand-900 p-1"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {customer.is_active ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const reason = prompt('Deactivation reason:');
                            if (reason || window.confirm('Deactivate without reason?')) handleDeactivate(customer, reason)
                          }}
                          disabled={actionLoading}
                          className="text-yellow-600 hover:text-yellow-900 px-2 py-1 text-xs"
                          title="Deactivate"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {e.stopPropagation(); handleActivate(customer)}}
                          disabled={actionLoading}
                          className="text-green-600 hover:text-green-900 px-2 py-1 text-xs"
                          title="Activate"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(customer)
                        }}
                        disabled={actionLoading}
                        className="text-red-600 hover:text-red-900 px-2 py-1 text-xs"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {customers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">{filter === 'all' ? 'No customers found' : `No ${filter} customers found`}</p>
            </div>
          )}
        </div>
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800">Customer Details</h3>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Name</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedCustomer.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Email</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Company</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedCustomer.company || 'Not specified'}</p>
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-slate-700">Subscription Plan</label>
                    <button
                      onClick={() => setPlanDropdownOpen(planDropdownOpen === 'modal' ? null : 'modal')}
                      disabled={actionLoading}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-brand-300 transition-all mt-1 ${getPlanBadgeClasses(selectedCustomer.subscription_plan)}`}
                      title="Click to change plan"
                    >
                      {planLabel(selectedCustomer.subscription_plan)}
                      <svg className="h-3 w-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {planDropdownOpen === 'modal' && (
                      <div className="absolute z-30 mt-1 w-36 bg-white rounded-md border border-slate-200 shadow-lg">
                        {PLAN_OPTIONS.map(({value: plan, label}) => (
                          <button
                            key={plan}
                            onClick={() => handleUpdatePlan(selectedCustomer, plan)}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-100 first:rounded-t-md last:rounded-b-md ${
                              selectedCustomer.subscription_plan === plan ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-700'
                            }`}
                          >
                            {label}
                            {selectedCustomer.subscription_plan === plan && ' (current)'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      !selectedCustomer.is_verified ? 'bg-yellow-100 text-yellow-800' :
                      selectedCustomer.is_active ? 'bg-green-50 text-green-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      {!selectedCustomer.is_verified ? 'Unverified' :
                       selectedCustomer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Usage This Month</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedCustomer.monthly_conversations_used || 0} / {selectedCustomer.monthly_conversations_limit || 1000} conversations</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Login Count</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedCustomer.login_count || 0}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Last Login</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedCustomer.last_login_at
                        ? new Date(selectedCustomer.last_login_at).toLocaleString()
                        : 'Never'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Registered</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedCustomer.created_at
                        ? new Date(selectedCustomer.created_at).toLocaleString()
                        : 'Unknown'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">API Key Status</label>
                    <p className="mt-1 text-sm text-slate-900">
                      {selectedCustomer.api_key_created ? (
                        <span className="text-green-600">Active</span>
                      ) : (
                        <span className="text-slate-500">Not Created</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Extend Trial Section - only for starter plan users */}
              {selectedCustomer.subscription_plan === 'starter' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-800 mb-1">Extend Trial</h4>
                  {selectedCustomer.subscription_end_date && (
                    <p className="text-xs text-blue-600 mb-3">
                      Current end date: {new Date(selectedCustomer.subscription_end_date).toLocaleDateString()}
                      {(() => {
                        const days = Math.ceil((new Date(selectedCustomer.subscription_end_date) - new Date()) / (1000 * 60 * 60 * 24))
                        return days <= 0
                          ? ' (Expired)'
                          : ` (${days}d remaining)`
                      })()}
                    </p>
                  )}
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={extendDays}
                      onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                      className="w-24 px-3 py-1.5 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Days"
                    />
                    <span className="text-sm text-blue-700">days</span>
                    <button
                      onClick={() => handleExtendTrial(selectedCustomer)}
                      disabled={extendLoading || !extendDays || extendDays < 1}
                      className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {extendLoading ? 'Extending...' : 'Add Days'}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium hover:bg-slate-400"
                >
                  Close
                </button>
                {selectedCustomer.is_active ? (
                  <button
                    onClick={() => handleDeactivate(selectedCustomer)}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700"
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Deactivate'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleActivate(selectedCustomer)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700"
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Activate'}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedCustomer)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700"
                  disabled={actionLoading}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
